import type { AreaItem } from './missionModels';

type SlamPlacementLike = {
    lat: number;
    lng: number;
    bearingDegrees: number;
};

const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS = 6371000;
const MAX_DIRECT_GPS_DISTANCE_METERS = 5000;

const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const isGpsCoordinate = (coord: any): boolean => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const lng = toFiniteNumber(coord[0]);
    const lat = toFiniteNumber(coord[1]);
    return lng !== null && lat !== null
        && lng >= -180 && lng <= 180
        && lat >= -90 && lat <= 90;
};

const collectCoordinates = (geom: any): Array<[number, number]> => {
    const coordinates: Array<[number, number]> = [];
    const collect = (value: any) => {
        if (!Array.isArray(value)) return;
        const first = toFiniteNumber(value[0]);
        const second = toFiniteNumber(value[1]);
        if (first !== null && second !== null) {
            coordinates.push([first, second]);
            return;
        }
        value.forEach(collect);
    };
    collect(geom?.coordinates);
    return coordinates;
};

const isGpsGeometry = (geom: any): boolean => {
    if (!geom?.coordinates) return false;
    if (geom.type === 'Point') return isGpsCoordinate(geom.coordinates);
    const coordinates = collectCoordinates(geom);
    return coordinates.length > 0 && coordinates.every(isGpsCoordinate);
};

const getGeometryCenter = (geom: any): { lng: number; lat: number } | null => {
    const coordinates = collectCoordinates(geom);
    if (!coordinates.length) return null;
    const sum = coordinates.reduce(
        (accumulator, [lng, lat]) => ({
            lng: accumulator.lng + lng,
            lat: accumulator.lat + lat,
        }),
        { lng: 0, lat: 0 }
    );
    return {
        lng: sum.lng / coordinates.length,
        lat: sum.lat / coordinates.length,
    };
};

const distanceMeters = (
    from: { lat: number; lng: number },
    to: { lat: number; lng: number }
): number => {
    const dLat = (to.lat - from.lat) * DEG_TO_RAD;
    const dLng = (to.lng - from.lng) * DEG_TO_RAD;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(from.lat * DEG_TO_RAD) * Math.cos(to.lat * DEG_TO_RAD) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return EARTH_RADIUS * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isGpsGeometryNearAnchor = (geom: any, anchor: SlamPlacementLike): boolean => {
    if (!isGpsGeometry(geom)) return false;
    const center = getGeometryCenter(geom);
    if (!center) return false;
    return distanceMeters(anchor, center) <= MAX_DIRECT_GPS_DISTANCE_METERS;
};

const localMetersToGps = (mx: number, my: number, anchor: SlamPlacementLike): [number, number] => {
    const bearing = anchor.bearingDegrees * DEG_TO_RAD;
    const northM = my * Math.cos(bearing) - mx * Math.sin(bearing);
    const eastM = my * Math.sin(bearing) + mx * Math.cos(bearing);
    const lat = anchor.lat + (northM / EARTH_RADIUS) * (180 / Math.PI);
    const lng = anchor.lng + (eastM / (EARTH_RADIUS * Math.cos(anchor.lat * DEG_TO_RAD))) * (180 / Math.PI);
    return [lng, lat];
};

const convertLocalGeometryToGps = (geom: any, anchor: SlamPlacementLike): any | null => {
    if (!geom?.coordinates) return null;
    if (geom.type === 'Point') {
        const mx = toFiniteNumber(geom.coordinates[0]);
        const my = toFiniteNumber(geom.coordinates[1]);
        if (mx === null || my === null) return null;
        return { type: 'Point', coordinates: localMetersToGps(mx, my, anchor) };
    }
    if (geom.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
        return {
            type: 'Polygon',
            coordinates: geom.coordinates.map((ring: any[]) => (
                ring.map((coord) => {
                    const mx = toFiniteNumber(coord?.[0]);
                    const my = toFiniteNumber(coord?.[1]);
                    return mx === null || my === null ? null : localMetersToGps(mx, my, anchor);
                }).filter(Boolean)
            )),
        };
    }
    if (geom.type === 'MultiPolygon' && Array.isArray(geom.coordinates?.[0]?.[0])) {
        return {
            type: 'MultiPolygon',
            coordinates: geom.coordinates.map((polygon: any[][]) => (
                polygon.map((ring) => (
                    ring.map((coord) => {
                        const mx = toFiniteNumber(coord?.[0]);
                        const my = toFiniteNumber(coord?.[1]);
                        return mx === null || my === null ? null : localMetersToGps(mx, my, anchor);
                    }).filter(Boolean)
                ))
            )),
        };
    }
    return null;
};

const getAreaOwnPlacement = (area?: AreaItem | null): SlamPlacementLike | null => {
    const geoAnchor = area?.slamMap?.geoAnchor;
    if (geoAnchor) {
        const lat = toFiniteNumber(geoAnchor.lat);
        const lng = toFiniteNumber(geoAnchor.lng);
        const bearingDegrees = toFiniteNumber(geoAnchor.bearingDegrees) ?? 0;
        if (lat !== null && lng !== null) {
            return { lat, lng, bearingDegrees };
        }
    }

    const saved = area?.configurations?.slamPlacement;
    const lat = toFiniteNumber(saved?.lat);
    const lng = toFiniteNumber(saved?.lng);
    const bearingDegrees = toFiniteNumber(saved?.bearingDegrees) ?? 0;
    if (lat !== null && lng !== null) {
        return { lat, lng, bearingDegrees };
    }

    return null;
};

const buildAreaLookup = (areas: AreaItem[], selectedArea?: AreaItem | null): Map<number, AreaItem> => {
    const lookup = new Map<number, AreaItem>();
    areas.forEach((area) => {
        lookup.set(area.id, area);
        if (area.parentArea?.id && !lookup.has(area.parentArea.id)) {
            lookup.set(area.parentArea.id, area.parentArea);
        }
    });
    if (selectedArea?.id) {
        lookup.set(selectedArea.id, {
            ...(lookup.get(selectedArea.id) || {}),
            ...selectedArea,
        });
    }
    return lookup;
};

const resolveNearestPlacement = (
    area: AreaItem,
    areaLookup: Map<number, AreaItem>,
    selectedArea?: AreaItem | null
): SlamPlacementLike | null => {
    const selectedPlacement = selectedArea?.id === area.id ? getAreaOwnPlacement(selectedArea) : null;
    if (selectedPlacement) return selectedPlacement;

    let current: AreaItem | null | undefined = area;
    let currentId: number | null | undefined = area.id;
    const visited = new Set<number>();

    while (current && currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const placement = getAreaOwnPlacement(current);
        if (placement) return placement;

        const parent: AreaItem | null = current.parentArea || (current.parentAreaId ? areaLookup.get(current.parentAreaId) || null : null);
        current = parent;
        currentId = parent?.id ?? null;
    }

    return getAreaOwnPlacement(selectedArea);
};

export const getGpsGeometryForOperationsMap = (
    area: AreaItem | null | undefined,
    allAreas: AreaItem[],
    selectedArea?: AreaItem | null
): any | null => {
    if (!area?.geom) return null;

    const spaceType = String(area.spaceType || '').trim().toLowerCase();
    if (spaceType !== 'indoor') {
        return isGpsGeometry(area.geom) ? area.geom : null;
    }

    const areaLookup = buildAreaLookup(allAreas, selectedArea);
    const anchor = resolveNearestPlacement(area, areaLookup, selectedArea);
    if (!anchor) {
        return null;
    }

    if (isGpsGeometryNearAnchor(area.geom, anchor)) {
        return area.geom;
    }

    const converted = convertLocalGeometryToGps(area.geom, anchor);
    return isGpsGeometry(converted) ? converted : null;
};

export const getAreasForOperationsMap = (
    areasToDisplay: AreaItem[],
    allAreas: AreaItem[],
    selectedArea?: AreaItem | null
): AreaItem[] => (
    areasToDisplay
        .map((area) => ({
            ...area,
            geom: getGpsGeometryForOperationsMap(area, allAreas, selectedArea),
        }))
        .filter((area) => Boolean(area.geom))
);
